<template>
  <div
    class="vue-select"
    :class="{
      [$class('disabled')]: disabled,
      [$class('open')]: open,
    }"
  >
    <input
      type="hidden"
      :name="name"
      :value="selectedOption?.value"
    >

    <label
      id="vue-select-label"
      :class="$class('hidden')"
      v-text="label"
    />

    <div
      ref="comboEl"
      aria-controls="vue-select-list"
      :aria-expanded="open"
      aria-haspopup="listbox"
      aria-labelledby="vue-select-label"
      :aria-activedescendant="open ? `option-${selectedIndex}`: undefined"
      class="vue-select__input"
      role="combobox"
      tabindex="0"
      @blur="onComboBlur"
      @click="onComboClick"
      @keydown="onComboKeyDown"
    >
      <slot
        name="toggle"
        :selected-option="selectedOption"
      >
        <div class="vue-select__input-text">
          <span
            class="text-body-s-bold"
            v-text="selectedOption?.name"
          />
        </div>
        <icon-chevron-fill-down />
      </slot>
    </div>

    <div
      class="vue-select__menu"
      :class="alignment"
    >
      <div
        id="vue-select-list"
        ref="listboxEl"
        class="vue-select__list"
        role="listbox"
        aria-labelledby="vue-select-label"
        tabindex="-1"
        @focusout="onComboBlur"
      >
        <div
          v-for="(slot, index) of optionSlots"
          :id="`option-${index}`"
          ref="optionRefs"
          :key="index"
          role="option"
          class="vue-select__option"
          :class="{
            'option-current': activeIndex == index
          }"
          :aria-selected="selectedIndex == index"
          @click="() => onOptionClick(index)"
          @mousedown="onOptionMouseDown"
        >
          <slot :name="slot">
            {{ slot }}
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Vue select (vue-select)
 * -----------------------------------------------------------------------------
 * Single select combobox widget that is functionally similar to an HTML select
 * element.
 *
 */
import IconChevronFillDown from '~icons/directional-navigation/chevron-fill/down.svg'

export default {
  name: 'VueSelect',

  components: {
    IconChevronFillDown,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      default: () => [],
    },
    selectedIndex: {
      type: Number,
      default: 2,
    },
    alignment: {
      type: String,
      default: 'left',
    },
  },

  data() {
    return {
      // Save a list of named combobox actions, for future readability
      SelectActions: {
        Close: 0,
        CloseSelect: 1,
        First: 2,
        Last: 3,
        Next: 4,
        Open: 5,
        PageDown: 6,
        PageUp: 7,
        Previous: 8,
        Select: 9,
        Type: 10,
      },
      open: false,
      searchString: '',
      searchTimeout: null,
      activeIndex: 2,
      selectedOption: null,
    }
  },

  computed: {
    optionSlots() {
      return Object.keys(this.$slots).filter(slot => slot !== 'toggle')
    },
  },

  watch: {
    selectedIndex() {
      this.selectedOption = this.options[this.selectedIndex]
      this.activeIndex = this.selectedIndex
    },
  },

  mounted() {
    this.activeIndex = this.selectedIndex
    this.selectedOption = this.options[this.selectedIndex]
  },

  methods: {
    // filter an array of options against an input string
    // returns an array of options that begin with the filter string,
    // case-independent
    filterOptions(options = [], filter, exclude = []) {
      return options.filter((option) => {
        const matches = option.toLowerCase().indexOf(filter.toLowerCase()) === 0
        return matches && exclude.indexOf(option) < 0
      })
    },

    // map a key press to an action
    getActionFromKey(event, menuOpen) {
      const { key, altKey, ctrlKey, metaKey } = event
      // all keys that will do the default open action
      const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' ']
      // handle opening when closed
      if (!menuOpen && openKeys.includes(key)) {
        return this.SelectActions.Open
      }

      // home and end move the selected option when open or closed
      if (key === 'Home') {
        return this.SelectActions.First
      }
      if (key === 'End') {
        return this.SelectActions.Last
      }

      // handle typing characters when open or closed
      if (
        key === 'Backspace' ||
        key === 'Clear' ||
        (key.length === 1 && key !== ' ' && !altKey && !ctrlKey && !metaKey)
      ) {
        return this.SelectActions.Type
      }

      // handle keys when open
      if (menuOpen) {
        if (key === 'ArrowUp' && altKey) {
          return this.SelectActions.CloseSelect
        }

        if (key === 'ArrowDown' && !altKey) {
          return this.SelectActions.Next
        }

        if (key === 'ArrowUp') {
          return this.SelectActions.Previous
        }

        if (key === 'PageUp') {
          return this.SelectActions.PageUp
        }

        if (key === 'PageDown') {
          return this.SelectActions.PageDown
        }

        if (key === 'Escape') {
          return this.SelectActions.Close
        }

        if (key === 'Enter' || key === ' ') {
          return this.SelectActions.CloseSelect
        }
      }
    },

    // return the index of an option from an array of options,
    // based on a search string
    // if the filter is multiple iterations of the same letter (e.g "aaa"),
    // then cycle through first-letter matches
    getIndexByLetter(options, filter, startIndex = 0) {
      const optionNames = options.map(option => option?.name)
      const orderedOptions = [
        ...optionNames.slice(startIndex),
        ...optionNames.slice(0, startIndex),
      ]
      const firstMatch = this.filterOptions(orderedOptions, filter)[0]
      const allSameLetter = (array) => array.every((letter) => letter === array[0])

      // first check if there is an exact match for the typed string
      if (firstMatch) {
        return optionNames.indexOf(firstMatch)
      }

      // if the same letter is being repeated,
      // cycle through first-letter matches
      if (allSameLetter(filter.split(''))) {
        const matches = this.filterOptions(orderedOptions, filter[0])
        return optionNames.indexOf(matches[0])
      }

      // if no matches, return -1
      return -1
    },

    // get an updated option index after performing an action
    getUpdatedIndex(currentIndex, maxIndex, action) {
      // used for pageup/pagedown
      const pageSize = 10

      switch (action) {
        case this.SelectActions.First:
          return 0
        case this.SelectActions.Last:
          return maxIndex
        case this.SelectActions.Previous:
          return Math.max(0, currentIndex - 1)
        case this.SelectActions.Next:
          return Math.min(maxIndex, currentIndex + 1)
        case this.SelectActions.PageUp:
          return Math.max(0, currentIndex - pageSize)
        case this.SelectActions.PageDown:
          return Math.min(maxIndex, currentIndex + pageSize)
        default:
          return currentIndex
      }
    },

    // check if element is visible in browser view port
    isElementInView(element) {
      const bounding = element.getBoundingClientRect()

      return (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
        bounding.right <=
        (window.innerWidth || document.documentElement.clientWidth)
      )
    },

    // check if an element is currently scrollable
    isScrollable(element) {
      return element && element.clientHeight < element.scrollHeight
    },

    // ensure a given child element is within the parent's visible scroll area
    // if the child is not visible, scroll the parent
    maintainScrollVisibility(activeElement, scrollParent) {
      const { offsetHeight, offsetTop } = activeElement
      const { offsetHeight: parentOffsetHeight, scrollTop } = scrollParent

      const isAbove = offsetTop < scrollTop
      const isBelow = offsetTop + offsetHeight > scrollTop + parentOffsetHeight

      if (isAbove) {
        scrollParent.scrollTo(0, offsetTop)
      } else if (isBelow) {
        scrollParent.scrollTo(0, offsetTop - parentOffsetHeight + offsetHeight)
      }
    },

    getSearchString(char) {
      // reset typing timeout and start new timeout
      // this allows us to make multiple-letter matches, like a native select
      if (typeof this.searchTimeout === 'number') {
        window.clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = window.setTimeout(() => {
        this.searchString = ''
      }, 500)

      // add most recent letter to saved search string
      this.searchString += char
      return this.searchString
    },

    onComboBlur(event) {
      // do nothing if relatedTarget is contained within listboxEl
      if (this.$refs.listboxEl.contains(event.relatedTarget)) {
        return
      }

      // select current option and close
      if (this.open) {
        this.selectOption(this.activeIndex)
        this.updateMenuState(false, false)
      }
    },

    onComboClick() {
      this.updateMenuState(!this.open, false)
    },

    onComboKeyDown(event) {
      const { key } = event
      const max = this.options.length - 1

      const action = this.getActionFromKey(event, this.open)

      switch (action) {
        case this.SelectActions.Last:
        case this.SelectActions.First:
          this.updateMenuState(true)
        // intentional fallthrough
        case this.SelectActions.Next:
        case this.SelectActions.Previous:
        case this.SelectActions.PageUp:
        case this.SelectActions.PageDown:
          event.preventDefault()
          return this.onOptionChange(
            this.getUpdatedIndex(this.activeIndex, max, action),
          )
        case this.SelectActions.CloseSelect:
          event.preventDefault()
          this.selectOption(this.activeIndex)
        // intentional fallthrough
        case this.SelectActions.Close:
          event.preventDefault()
          return this.updateMenuState(false)
        case this.SelectActions.Type:
          return this.onComboType(key)
        case this.SelectActions.Open:
          event.preventDefault()
          return this.updateMenuState(true)
      }
    },

    onComboType(letter) {
      // open the listbox if it is closed
      this.updateMenuState(true)

      // find the index of the first matching option
      const searchString = this.getSearchString(letter)
      const searchIndex = this.getIndexByLetter(
        this.options,
        searchString,
        this.activeIndex + 1,
      )

      // if a match was found, go to it
      // if no matches, clear the timeout and search string
      if (searchIndex >= 0) {
        this.onOptionChange(searchIndex)
      } else {
        window.clearTimeout(this.searchTimeout)
        this.searchString = ''
      }
    },

    onOptionChange(index) {
      // update state
      this.activeIndex = index

      // ensure the new option is in view
      if (this.isScrollable(this.$refs.listboxEl)) {
        this.maintainScrollVisibility(this.$refs.optionRefs[index], this.$refs.listboxEl)
      }

      // ensure the new option is visible on screen
      // ensure the new option is in view
      if (!this.isElementInView(this.$refs.optionRefs[index])) {
        this.$refs.optionRefs[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    },

    onOptionClick(index) {
      this.onOptionChange(index)
      this.selectOption(index)
      this.updateMenuState(false)
    },

    onOptionMouseDown() {
      // Clicking an option will cause a blur event,
      // but we don't want to perform the default keyboard blur action
      this.ignoreBlur = true
    },

    selectOption(index) {
      // update state
      this.activeIndex = index

      // Do not fire change event if selectedIndex is not changed.
      if (this.selectedIndex === index) {
        return
      }

      this.$emit('change', index)
    },

    updateMenuState(open, callFocus = true) {
      if (this.open === open) {
        return
      }

      // update state
      this.open = open

      if (!open && !this.isElementInView(this.$refs.comboEl)) {
        this.$refs.comboEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }

      // move focus back to the combobox, if needed
      if (callFocus) {
        this.$refs.comboEl.focus()
      }
    },

  },
}
</script>

<style lang="scss">
@import './vue-select';
</style>
